import React, { useState } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertCircle,
  Users,
  Building,
  Globe,
  UserCog,
} from "lucide-react";
import { Team, TeamFormData, User } from "./types";
import { useLanguage } from "../../contexts/LanguageContext";

interface TeamsTabProps {
  teams: Team[];
  users: User[];
  loading: boolean;
  error: string | null;
  filters: any;
  onRefresh: () => void;
  onFilterChange: (filters: any) => void;
  onEditTeam: (team: Team) => void;
  onDeleteTeam: (teamId: string) => void;
}

export const TeamsTab: React.FC<TeamsTabProps> = ({
  teams,
  users,
  loading,
  error,
  filters,
  onRefresh,
  onFilterChange,
  onEditTeam,
  onDeleteTeam,
}) => {
  const { t } = useLanguage();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [formData, setFormData] = useState<TeamFormData>({
    name: "",
    type: "call-center",
    description: "",
    countries: [],
    supervisorId: "",
    members: [],
  });

  const [editFormData, setEditFormData] = useState<TeamFormData>({
    name: "",
    type: "call-center",
    description: "",
    countries: [],
    supervisorId: "",
    members: [],
  });

  const teamTypes = [
    { value: "call-center", label: t("users.teamTypeCallCenter") },
    { value: "post-office", label: t("users.teamTypePostOffice") },
    { value: "management", label: t("users.teamTypeManagement") },
    { value: "support", label: t("users.teamTypeSupport") },
  ];

  const availableCountries = [
    "Albania",
    "Bulgaria",
    "Greece",
    "Kosovo",
    "Serbia",
    "The Republic of Macedonia",
  ];

  const handleCreateTeam = async () => {
    setCreating(true);
    try {
      // Implementation for creating team
      console.log("Creating team:", formData);
      setShowCreateModal(false);
      onRefresh();
    } catch (error) {
      console.error("Error creating team:", error);
    } finally {
      setCreating(false);
    }
  };

  const handleEditTeam = async () => {
    setUpdating(true);
    try {
      // Implementation for updating team
      console.log("Updating team:", editFormData);
      setShowEditModal(false);
      onRefresh();
    } catch (error) {
      console.error("Error updating team:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (window.confirm(t("users.deleteTeamConfirm"))) {
      try {
        // Implementation for deleting team
        console.log("Deleting team:", teamId);
        onRefresh();
      } catch (error) {
        console.error("Error deleting team:", error);
      }
    }
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    setEditFormData({
      name: team.name,
      type: team.type,
      description: team.description,
      countries: team.countries,
      supervisorId: team.supervisorId,
      members: team.members,
    });
    setShowEditModal(true);
  };

  const filteredTeams = teams.filter((team) => {
    const matchesSearch =
      team.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      team.description.toLowerCase().includes(filters.searchTerm.toLowerCase());

    const matchesType =
      filters.selectedType === "all" || team.type === filters.selectedType;
    const matchesCountry =
      filters.selectedCountry === "all" ||
      team.countries.includes(filters.selectedCountry);

    return matchesSearch && matchesType && matchesCountry;
  });

  const getSupervisorName = (supervisorId: string) => {
    const supervisor = users.find((user) => user.id === supervisorId);
    return supervisor
      ? `${supervisor.profile.firstName} ${supervisor.profile.lastName}`
      : "Unknown";
  };

  const getTeamMembersCount = (members: string[]) => {
    return members.length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
        <span className="ml-2 text-gray-600">{t("users.loadingTeams")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            {t("users.teamsManagement")}
          </h2>
          <p className="text-sm text-gray-600">
            {t("users.teamsManagementDescription")}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("users.addTeam")}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.search")}
            </label>
            <input
              type="text"
              placeholder={t("users.searchTeams")}
              value={filters.searchTerm}
              onChange={(e) =>
                onFilterChange({ ...filters, searchTerm: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.type")}
            </label>
            <select
              value={filters.selectedType}
              onChange={(e) =>
                onFilterChange({ ...filters, selectedType: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            >
              <option value="all">{t("users.allTypes")}</option>
              {teamTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.country")}
            </label>
            <select
              value={filters.selectedCountry}
              onChange={(e) =>
                onFilterChange({ ...filters, selectedCountry: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            >
              <option value="all">{t("users.allCountries")}</option>
              {availableCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableTeam")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.type")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableSupervisor")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableMembers")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableCountries")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTeams.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Building className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {team.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {team.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {team.type
                          .replace("-", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserCog className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {getSupervisorName(team.supervisorId)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {getTeamMembersCount(team.members)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {team.countries.slice(0, 2).map((country) => (
                          <span
                            key={country}
                            className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full"
                          >
                            {country}
                          </span>
                        ))}
                        {team.countries.length > 2 && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                            +{team.countries.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(team)}
                          className="text-[#264a3e] hover:text-[#1e3a32]"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("users.createNewTeam")}
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("users.teamName")}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  >
                    {teamTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("users.description")}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
                >
                  {t("users.cancel")}
                </button>
                <button
                  onClick={handleCreateTeam}
                  disabled={creating}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#264a3e] border border-transparent rounded-md hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
                >
                  {creating ? t("users.creating") : t("users.createTeam")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && editingTeam && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("users.editTeam")}</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={editFormData.type}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  >
                    {teamTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditTeam}
                  disabled={updating}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#264a3e] border border-transparent rounded-md hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
                >
                  {updating ? "Updating..." : "Update Team"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsTab;
